import React, { useState } from 'react';
import { Button } from './ui/button';
import { Image, Music, Palette, Map } from 'lucide-react';
import { assetCategories, environmentThemes, audioTypes } from '../data/mockData';

const AssetsSection = () => {
  const [activeTheme, setActiveTheme] = useState(0);

  return (
    <section id="assets" className="py-24 bg-gradient-to-b from-black via-gray-950 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Generate & Organize
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> All Game Assets</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            All your game assets — AI-made, organized, and ready to use.
          </p>
        </div>

        {/* Asset Categories Grid */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Animation */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Image size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Animation</h3>
              </div>
              <div className={`bg-gradient-to-br ${assetCategories[0].gradient} rounded-lg overflow-hidden aspect-video flex items-center justify-center`}>
                <div className="text-6xl">{assetCategories[0].icon}</div>
              </div>
            </div>

            {/* VFX */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-pink-600 rounded-lg flex items-center justify-center">
                  <Palette size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">VFX</h3>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {assetCategories[1].gradients.map((item, index) => (
                  <div key={index} className={`bg-gradient-to-br ${item.gradient} rounded-lg overflow-hidden aspect-square flex items-center justify-center`}>
                    <div className="text-3xl">{item.icon}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Roles */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Image size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Roles</h3>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {assetCategories[2].gradients.map((item, index) => (
                  <div key={index} className={`bg-gradient-to-br ${item.gradient} rounded-lg overflow-hidden aspect-square flex items-center justify-center`}>
                    <div className="text-3xl">{item.icon}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tileset */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                  <Map size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Tileset</h3>
              </div>
              <div className={`bg-gradient-to-br ${assetCategories[3].gradient} rounded-lg overflow-hidden aspect-video flex items-center justify-center`}>
                <div className="text-6xl">{assetCategories[3].icon}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Environment Themes */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">Objects & Environment Themes</h3>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-white/10">
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              {environmentThemes.map((theme, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTheme(index)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    activeTheme === index
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {theme.name}
                </button>
              ))}
            </div>
            <div className="h-48 rounded-xl flex items-center justify-center" style={{ backgroundColor: environmentThemes[activeTheme].color + '20' }}>
              <div className="text-center">
                <div className="text-4xl mb-2">🎨</div>
                <div className="text-white font-semibold">{environmentThemes[activeTheme].name}</div>
                <div className="text-gray-400 text-sm">Theme Preview</div>
              </div>
            </div>
          </div>
        </div>

        {/* Audio Section */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">BGM & Sound Effects</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Music size={20} className="text-white" />
                </div>
                <h4 className="text-xl font-bold text-white">Background Music</h4>
              </div>
              <div className="space-y-3">
                <div className="bg-black/50 rounded-lg p-4 flex items-center justify-between">
                  <span className="text-gray-300">Epic Battle Theme</span>
                  <button className="text-purple-400 hover:text-purple-300">
                    <Music size={18} />
                  </button>
                </div>
                <div className="bg-black/50 rounded-lg p-4 flex items-center justify-between">
                  <span className="text-gray-300">Peaceful Village</span>
                  <button className="text-purple-400 hover:text-purple-300">
                    <Music size={18} />
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-pink-600 rounded-lg flex items-center justify-center">
                  <Music size={20} className="text-white" />
                </div>
                <h4 className="text-xl font-bold text-white">Sound Effects</h4>
              </div>
              <div className="space-y-3">
                {audioTypes.map((audio, index) => (
                  <div key={index} className="bg-black/50 rounded-lg p-4 flex items-center justify-between">
                    <span className="text-gray-300">{audio.name}</span>
                    <button className="text-pink-400 hover:text-pink-300">
                      <Music size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            data-testid="assets-try-free-btn"
            onClick={() => window.location.href = '/create'}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8"
          >
            Try it Free
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AssetsSection;